document.addEventListener('alpine:init', () => {
  Alpine.data('mainPageSales', () => ({
    activeSection: '1',
    openFaqId: null,
    newsletterEmail: '',
    newsletterError: '',
    newsletterStatus: '',
    newsletterSubmitting: false,
    rootElement: null,

    init() {
      this.rootElement = this.$root || this.$el;

      this.$nextTick(() => {
        this.hideLinkedNewsletterSection();
        this.setupEditorBlockSelection();
        this.syncSectionFromHash();
      });
    },

    scrollToSection(sectionId) {
      const rootElement = this.getRootElement();
      const targetElement = rootElement?.querySelector(`.content-section[data-section="${sectionId}"]`);
      if (!targetElement) {
        return;
      }

      this.activeSection = sectionId;

      const headerHeight = this.getHeaderHeight();
      if (headerHeight > 0) {
        targetElement.style.scrollMarginTop = `${headerHeight}px`;
      }

      const nextHash = targetElement.id ? `#${targetElement.id}` : '';
      if (nextHash && window.history?.replaceState) {
        window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}${nextHash}`);
      }

      targetElement.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'start',
      });
    },

    toggleFaq(faqId) {
      if (this.isFaqOpen(faqId)) {
        this.openFaqId = null;
        return;
      }

      this.openFaqId = faqId;
    },

    isFaqOpen(faqId) {
      return this.openFaqId === faqId;
    },

    syncFaqOpenState(element, faqId) {
      if (!element) {
        return;
      }

      const shouldBeOpen = this.isFaqOpen(faqId);
      const isOpen = element.hasAttribute('open');

      if (shouldBeOpen === isOpen) {
        return;
      }

      if (shouldBeOpen) {
        element.setAttribute('open', '');
        return;
      }

      element.removeAttribute('open');
    },

    async submitNewsletter() {
      if (this.newsletterSubmitting) {
        return;
      }

      const email = this.newsletterEmail.trim();
      this.newsletterError = '';
      this.newsletterStatus = '';

      if (!this.isValidEmail(email)) {
        this.newsletterError = 'Enter a valid email address.';
        this.$nextTick(() => this.$refs.newsletterEmailInput?.focus());
        return;
      }

      this.newsletterSubmitting = true;

      try {
        const result = await this.subscribeToOmnisend(email);

        if (result.success) {
          this.newsletterStatus = 'Thanks for subscribing. You will receive our latest offers soon.';
          this.newsletterEmail = '';
        } else {
          this.newsletterError = 'Subscription failed. Please try again in a moment.';
          console.error('Subscription failed:', result.error);
        }
      } catch (error) {
        this.newsletterError = 'Something went wrong. Please try again later.';
        console.error('Subscription error:', error);
      } finally {
        this.newsletterSubmitting = false;
      }
    },

    hideLinkedNewsletterSection() {
      const hiddenSectionId = this.getRootElement()?.dataset.hideNewsletterSectionId;
      if (!hiddenSectionId) {
        return;
      }

      const targetSection = document.getElementById(hiddenSectionId);
      if (targetSection) {
        targetSection.style.display = 'none';
      }
    },

    setupEditorBlockSelection() {
      const handler = (event) => {
        this.handleEditorBlockSelect(event);
      };

      document.addEventListener('shopify:block:select', handler);
    },

    syncSectionFromHash() {
      const hash = window.location.hash;
      if (!hash) {
        return;
      }

      const targetElement = this.getRootElement()?.querySelector(hash);
      if (targetElement?.dataset.section) {
        this.activeSection = targetElement.dataset.section;
      }
    },

    handleEditorBlockSelect(event) {
      const rootElement = this.getRootElement();

      if (!rootElement?.isConnected) {
        return;
      }

      const selectedBlockId = event.detail?.blockId;
      let targetElement = null;

      if (selectedBlockId) {
        targetElement = rootElement.querySelector(`[data-sales-block-id="${selectedBlockId}"]`);
      }

      if (!targetElement && event.target?.closest) {
        targetElement = event.target.closest('[data-sales-block-id]');
      }

      if (!targetElement || !rootElement.contains(targetElement)) {
        return;
      }

      const targetSection = targetElement.dataset.section;
      if (targetSection) {
        this.activeSection = targetSection;
      }

      targetElement.scrollIntoView({
        behavior: event.detail?.load || window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'center',
      });
    },

    getHeaderHeight() {
      const selectors = ['.shopify-section--announcement-bar', '.shopify-section--header'];

      return selectors.reduce((total, selector) => {
        const element = document.querySelector(selector);
        return total + (element ? element.offsetHeight : 0);
      }, 0);
    },

    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    },

    getRootElement() {
      return this.rootElement || this.$root || this.$el;
    },

    async subscribeToOmnisend(email) {
      const apiKey = this.getRootElement()?.dataset.omnisendApiKey;
      if (!apiKey) {
        return {
          success: false,
          error: 'Omnisend API key is not configured.',
        };
      }

      try {
        const payload = {
          email,
          status: 'subscribed',
          statusIfNew: 'subscribed',
          statusDate: new Date().toISOString(),
        };

        const response = await fetch('https://api.omnisend.com/v3/contacts', {
          method: 'POST',
          headers: {
            'X-API-KEY': apiKey,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        const responseText = await response.text();

        console.log('Omnisend API status:', response.status);
        console.log('Omnisend API response:', responseText);

        if (response.status === 200 || response.status === 201) {
          return {
            success: true,
            message: 'User successfully subscribed to Omnisend account',
          };
        }

        return {
          success: false,
          error: `API request failed, response code: ${response.status}, response: ${responseText}`,
        };
      } catch (error) {
        return {
          success: false,
          error: `API call exception: ${error.toString()}`,
        };
      }
    },
  }));
});
